import { type BillingPlanResponse } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface CurrentPlanCardProps {
  plan: BillingPlanResponse;
}

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  "usage_only_monthly": "Usage Only",
  "basic_monthly": "Basic Monthly",
  "basic_yearly": "Basic Yearly",
  "premium_monthly": "Premium Monthly",
  "premium_yearly": "Premium Yearly",
};

const PLAN_FEATURES: Record<string, string[]> = {
  "usage_only_monthly": [
    "Access to all podcasts",
    "~$0.01 per chat request",
    "~$0.10 per transcription compute-second",
    "No included credits",
  ],
  "basic_monthly": [
    "Access to all podcasts",
    "$2 monthly credit included",
    "Standard support",
    "Usage rates apply after credit",
  ],
  "basic_yearly": [
    "Access to all podcasts",
    "$20 yearly credit included",
    "Standard support",
    "Usage rates apply after credit",
  ],
  "premium_monthly": [
    "Access to all podcasts",
    "$4 monthly credit included",
    "Auto email summaries for new episodes",
    "Priority support",
    "Usage rates apply after credit",
  ],
  "premium_yearly": [
    "Access to all podcasts",
    "$30 yearly credit included",
    "Auto email summaries for new episodes",
    "Priority support",
    "Usage rates apply after credit",
  ],
};

function getPlanDisplayName(planKey: string | null): string {
  if (!planKey) return "No plan";
  return PLAN_DISPLAY_NAMES[planKey] || planKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

function getPlanFeatures(planKey: string | null): string[] {
  if (!planKey) return [];
  return PLAN_FEATURES[planKey] || [];
}

function StatusBadge({ status }: { status: BillingPlanResponse["subscription_status"] }) {
  const statusConfig = {
    active: {
      label: "Active",
      className: cn(theme.colors.primaryOrange, "text-gray-900 dark:text-white"),
    },
    canceled: {
      label: "Canceled",
      className: "bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white",
    },
    pending: {
      label: "Pending",
      className: "bg-yellow-200 dark:bg-yellow-800 text-gray-900 dark:text-white",
    },
    past_due: {
      label: "Past Due",
      className: cn(theme.colors.errorBg, theme.text.error),
    },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span
      className={cn(
        "inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold",
        theme.borders.neubrutalism,
        theme.shadows.neubrutalismSm,
        config.className,
      )}
    >
      ● {config.label}
    </span>
  );
}

export default function CurrentPlanCard({ plan }: CurrentPlanCardProps) {
  const planName = getPlanDisplayName(plan.stripe_plan_lookup_key);
  const features = getPlanFeatures(plan.stripe_plan_lookup_key);

  return (
    <section className={cn(theme.cards.base, "p-6")}>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Current Plan</h2>
      <div className="space-y-4">
        <div>
          <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{planName}</h3>
          <StatusBadge status={plan.subscription_status} />
        </div>
        {plan.subscription_status === "active" && features.length > 0 && (
          <ul className="space-y-2 mt-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className="text-primary-orange dark:text-primary-orange-dark">•</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
        {plan.subscription_status === "canceled" && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
            Your subscription has been canceled. You can resubscribe at any time.
          </p>
        )}
        {plan.subscription_status === "pending" && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
            Your subscription is being set up. This may take a few moments.
          </p>
        )}
        {plan.subscription_status === "past_due" && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-4">
            Please update your payment method to continue using the service.
          </p>
        )}
      </div>
    </section>
  );
}



